import type { CategoryKey, ScoreKey } from "~/sdks/LocalContent/types";
import type { TransformedScoresMenuData } from "~/sdks/LocalContent/utils/transformScoresMenuData/types";
import type { ScoresData, ScoreItem, Score } from "./types";

const MAX_SCORE_VALUE = 10;
const SCORE_MULTIPLIER = 2;

/**
 * Transforms the API response from the scores endpoint into a more usable format.
 *
 * @param data
 * @param scoresMenu
 * @returns
 */
export function transformScoresAPIResponse(
  data: ScoresData,
  scoresMenu: TransformedScoresMenuData
) {
  if (typeof data?.location === "undefined" || !scoresMenu)
    return {} as ScoreItem;

  const scores: ScoreItem = {} as ScoreItem;

  Object.keys(data.location).forEach((key) => {
    const singleScoreData = data?.location?.[key as ScoreKey];

    const category = scoresMenu.categories.find(([_key, categoryData]) =>
      categoryData.scores.some(([scoreKey]) => scoreKey === key)
    );

    let singleScoreValue =
      parseFloat(singleScoreData?.value || "0") * SCORE_MULTIPLIER;

    if (Number.isNaN(singleScoreValue)) {
      singleScoreValue = 0;
    } else if (singleScoreValue > MAX_SCORE_VALUE) {
      singleScoreValue = MAX_SCORE_VALUE;
    }

    const geographies: Score["geographies"] = {};

    if (typeof data?.geographies !== "undefined") {
      data.geographies.forEach((geography) => {
        if (
          typeof geography?.scores !== "undefined" &&
          typeof geography.scores[key as ScoreKey] !== "undefined"
        ) {
          const geographyValue =
            parseFloat(geography.scores[key as ScoreKey]?.value || "0") *
            SCORE_MULTIPLIER;

          if (typeof geography?.geog_id === "string") {
            geographies[geography.geog_id.split("_")[0]] = {
              text: geography.name,
              value: Math.round(geographyValue * 10) / 10,
            };
          }
        }
      });
    }

    scores[key as ScoreKey] = {
      text: singleScoreData?.text,
      value: Math.round(singleScoreValue * 10) / 10,
      geographies,
      categoryKey: category ? (category[0] as CategoryKey) : null,
      categoryOptions: category ? category[1] : null,
    };
  });

  return scores;
}
